import React, { useEffect, useState } from 'react';

import ModalShell from './ModalShell';
import { ProductKey, ProductSelectionContent } from './ProductSelectionModal';
import { DemoRequestFormContent } from './DemoRequestModal';
import { DemoVariant } from './demoRequestForm';
import { trackCta, CtaProduct, AppSurface } from '../../../Utils/analytics';

const PRODUCT_TO_CTA_PRODUCT: Record<ProductKey, CtaProduct> = {
	'assessment-service': 'assessment',
	mycoach: 'my_coach'
};

// Every 'assessment-service' pick in this modal routes to the business form variant.
const PRODUCT_TO_APP_SURFACE: Partial<Record<ProductKey, AppSurface>> = {
	'assessment-service': 'business'
};

const PRODUCT_LABELS: Record<ProductKey, string> = {
	'assessment-service': 'Assessment Service',
	mycoach: 'MyCoach (L&D)'
};

export interface BookDemoFlowProps {
	open: boolean;
	onClose: () => void;
}

type Step = { kind: 'branch' } | { kind: 'form'; variant: DemoVariant };

const PRODUCT_TO_VARIANT: Record<ProductKey, DemoVariant> = {
	'assessment-service': 'business',
	mycoach: 'mycoach'
};

/**
 * Branch-then-form flow (FE-004 -> FE-005): one dialog stays mounted for the
 * whole flow — selecting a product swaps the inner content in place rather
 * than closing and reopening a new modal.
 */
const BookDemoFlow = ({ open, onClose }: BookDemoFlowProps) => {
	const [step, setStep] = useState<Step>({ kind: 'branch' });

	useEffect(() => {
		if (open) return;
		// Let the close transition finish before resetting, so the outgoing
		// content doesn't visibly flash back to the branch step.
		const timer = setTimeout(() => setStep({ kind: 'branch' }), 200);
		return () => clearTimeout(timer);
	}, [open]);

	const handleSelect = (product: ProductKey) => {
		trackCta('demo product selected', {
			cta_label: PRODUCT_LABELS[product],
			product: PRODUCT_TO_CTA_PRODUCT[product],
			app_surface: PRODUCT_TO_APP_SURFACE[product],
			location: 'demo_modal'
		});
		setStep({ kind: 'form', variant: PRODUCT_TO_VARIANT[product] });
	};

	return (
		<ModalShell open={open} onClose={onClose}>
			{step.kind === 'branch' ? (
				<ProductSelectionContent onSelect={handleSelect} />
			) : (
				<DemoRequestFormContent variant={step.variant} open={open} />
			)}
		</ModalShell>
	);
};

export default BookDemoFlow;
